import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BibleVersionSelector from './components/BibleVersionSelector';
import BookChapterSelector from './components/BookChapterSelector';
import BibleTextDisplay from './components/BibleTextDisplay';
import ReadingProgressTracker from './components/ReadingProgressTracker';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { getBibleReaderState, saveBibleReaderState } from '../../utils/cookieManager';
import { trackBibleChapterNavigation, trackBibleBookChange } from '../../utils/ga';
import EGWInsightsDisplay from './components/EGWInsightsDisplay';
import CommentariesVerseInsights from './components/CommentariesVerseInsights';
import TSKDisplay from './components/TSKDisplay';
import { AudioPlayerProvider } from '../../contexts/AudioPlayerContext';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';
import AudioPlayerDrawer from '../../components/audio/AudioPlayerDrawer';
import FloatingPlayButton from '../../components/audio/FloatingPlayButton';
import BibleSearchDrawer from './components/BibleSearchDrawer';
import { bibleService } from '../../services/bibleService';
import GuestBanner from '../../components/GuestBanner';

// ── Navigation History Panel ──────────────────────────────────────────────────
const NavigationHistoryPanel = ({ history, onNavigate, onClear }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (history?.length === 0) return;
    const text = history?.map((entry, i) => `${i + 1}. ${entry?.label}${entry?.verse ? `:${entry?.verse}` : ''}`)?.join('\n');
    navigator.clipboard?.writeText(text)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {history?.length} {history?.length === 1 ? 'entry' : 'entries'}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            disabled={history?.length === 0}
            title="Copy history to clipboard"
            className="flex items-center gap-1 px-2 py-1 rounded text-xs border border-border bg-background hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon name={copied ? 'Check' : 'Copy'} size={12} />
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={onClear}
            disabled={history?.length === 0}
            title="Clear history"
            className="flex items-center gap-1 px-2 py-1 rounded text-xs border border-border bg-background hover:bg-muted text-destructive hover:text-destructive transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon name="Trash2" size={12} />
            Clear
          </button>
        </div>
      </div>
      {/* History list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {history?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center px-4">
            <Icon name="History" size={28} className="text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">No navigation history yet.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Navigate to verses or chapters to build your breadcrumb trail.</p>
          </div>
        ) : (
          history?.map((entry, index) => {
            const isLatest = index === history?.length - 1;
            return (
              <button
                key={entry?.id}
                onClick={() => onNavigate(entry)}
                className={`w-full text-left flex items-start gap-2 px-2.5 py-2 rounded-md border transition-colors group ${
                  isLatest
                    ? 'border-primary/40 bg-primary/8 text-foreground'
                    : 'border-transparent hover:border-border hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {/* Step number */}
                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                  isLatest ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary'
                }`}>
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold truncate">{entry?.label}</span>
                    {isLatest && (
                      <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-1 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{entry?.timestamp}</div>
                </div>
                <Icon name="ArrowRight" size={12} className="flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// Inner component that uses the audio context
const BibleReaderInner = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize directly from saved cookie state to avoid a Genesis 1 flash
  // on session restore. Using lazy initializers means the correct book/chapter
  // is available on the very first render, preventing stale annotation loading.
  const [selectedVersion, setSelectedVersion] = useState(() => {
    const savedState = getBibleReaderState();
    if (savedState?.version) {
      const oldNameMap = {
        "kjv + strong's": 'kjv',
        "kjv strongs": 'kjv',
        "kjv+strongs": 'kjv',
        "rv1909 s": 'rv1909',
        "rv1909+strongs": 'rv1909',
        "rv 1909 s": 'rv1909',
        // ASV Strong's → ASV"asv strongs": 'asv', "asv strong's": 'asv', "asv+strongs": 'asv',
        // Chinese Union Strong's → base version
        "cuv strongs": 'cuv',"cuv strong's": 'cuv',"chinese union strongs": 'chinese union',"chinese union strong's": 'chinese union',"cunp strongs": 'cunp',"cu strongs": 'cu',
        // Chinese Union (Simp) Strong's → Chinese Union (Simp)
        "chinese union (simp) strongs": 'chinese union (simp)',"chinese union (simp) strong's": 'chinese union (simp)',
      };
      const normalized = savedState?.version?.toLowerCase()?.trim();
      return oldNameMap?.[normalized] || savedState?.version;
    }
    return 'kjv';
  });
  const [selectedBook, setSelectedBook] = useState(() => {
    // Check URL query params first (from lexicon verse links)
    const params = new URLSearchParams(window.location.search);
    const urlBook = params?.get('book');
    if (urlBook) return urlBook;
    // Then check localStorage nav target (set by LexiconModal)
    try {
      const navTarget = localStorage.getItem('bibleReaderNavTarget');
      if (navTarget) {
        const parsed = JSON.parse(navTarget);
        if (parsed?.book) return parsed?.book;
      }
    } catch { /* ignore */ }
    const savedState = getBibleReaderState();
    return savedState?.book || 'Genesis';
  });
  const [selectedChapter, setSelectedChapter] = useState(() => {
    // Check URL query params first
    const params = new URLSearchParams(window.location.search);
    const urlChapter = params?.get('chapter');
    if (urlChapter) {
      const ch = parseInt(urlChapter, 10);
      if (!isNaN(ch) && ch > 0) return ch;
    }
    // Then check localStorage nav target
    try {
      const navTarget = localStorage.getItem('bibleReaderNavTarget');
      if (navTarget) {
        const parsed = JSON.parse(navTarget);
        if (parsed?.chapter) {
          const ch = parseInt(parsed?.chapter, 10);
          if (!isNaN(ch) && ch > 0) return ch;
        }
      }
    } catch { /* ignore */ }
    const savedState = getBibleReaderState();
    if (savedState?.chapter) {
      const chapterNum = parseInt(savedState?.chapter, 10);
      if (!isNaN(chapterNum) && chapterNum > 0) return chapterNum;
    }
    return 1;
  });

  const [showSidebar, setShowSidebar] = useState(true);
  const [activePanel, setActivePanel] = useState('egw-insights');
  const [highlightVerseNumber, setHighlightVerseNumber] = useState(() => {
    // Check URL query params first
    const params = new URLSearchParams(window.location.search);
    const urlVerse = params?.get('verse');
    if (urlVerse) {
      const v = parseInt(urlVerse, 10);
      if (!isNaN(v) && v > 0) return v;
    }
    // Then check localStorage nav target
    try {
      const navTarget = localStorage.getItem('bibleReaderNavTarget');
      if (navTarget) {
        const parsed = JSON.parse(navTarget);
        if (parsed?.verse) {
          const v = parseInt(parsed?.verse, 10);
          if (!isNaN(v) && v > 0) return v;
        }
      }
    } catch { /* ignore */ }
    return null;
  });
  const [selectedVerseForStudy, setSelectedVerseForStudy] = useState(null);
  const [showSearchDrawer, setShowSearchDrawer] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Version metadata: language and hasStrongs
  const [versionLanguage, setVersionLanguage] = useState('English');
  const [versionHasStrongs, setVersionHasStrongs] = useState(true);

  // Localized book name for the current book + language
  const [localizedBookName, setLocalizedBookName] = useState(null);

  // Navigation history state
  const [navHistory, setNavHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('bibleNavHistory');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const { openPlayer, isPlaying, isLoaded, activeBook, activeChapter, seekToVerse, activeVerse, registerChapterChangeCallback } = useAudioPlayer();

  // Handle URL query params from lexicon verse links (on mount and location change)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlBook = params?.get('book');
    const urlChapter = params?.get('chapter');
    const urlVerse = params?.get('verse');

    if (urlBook) {
      let chapter = urlChapter ? parseInt(urlChapter, 10) : 1;
      let verse = urlVerse ? parseInt(urlVerse, 10) : null;
      setSelectedBook(urlBook);
      setSelectedChapter(isNaN(chapter) ? 1 : chapter);
      if (verse && !isNaN(verse)) {
        setHighlightVerseNumber(verse);
        setTimeout(() => {
          const el = document?.getElementById(`verse-${verse}`);
          if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
      // Clear the URL params after consuming them
      navigate('/bible-reader', { replace: true });
    }

    // Clear localStorage nav target after consuming
    try {
      localStorage.removeItem('bibleReaderNavTarget');
    } catch { /* ignore */ }
  }, [location?.search]);

  // Persist history to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('bibleNavHistory', JSON.stringify(navHistory));
    } catch { /* ignore */ }
  }, [navHistory]);

  // Add a history entry when book/chapter changes
  const addHistoryEntry = useCallback((book, chapter, verse = null) => {
    const label = verse
      ? `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse}`
      : `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}`;
    const now = new Date();
    const timestamp = now?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const entry = {
      id: `${Date.now()}-${Math.random()}`,
      book,
      chapter,
      verse,
      label,
      timestamp,
    };
    setNavHistory(prev => {
      // Avoid duplicate consecutive entries
      const last = prev?.[prev?.length - 1];
      if (last && last?.book === book && last?.chapter === chapter && last?.verse === verse) return prev;
      // Keep max 100 entries
      const updated = [...prev, entry];
      return updated?.length > 100 ? updated?.slice(updated?.length - 100) : updated;
    });
  }, []);

  // Register callback so audio player can advance Bible Reader text
  useEffect(() => {
    registerChapterChangeCallback((book, chapter) => {
      setSelectedBook(book);
      setSelectedChapter(chapter);
      setHighlightVerseNumber(null);
      addHistoryEntry(book, chapter);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [registerChapterChangeCallback, addHistoryEntry]);

  // Save Bible reader state to cookies whenever book, chapter, or version changes
  useEffect(() => {
    saveBibleReaderState(selectedBook, selectedChapter, selectedVersion);
  }, [selectedBook, selectedChapter, selectedVersion]);

  // Fetch localized book name when book or language changes
  useEffect(() => {
    const fetchLocalizedName = async () => {
      if (!versionLanguage || versionLanguage?.toLowerCase() === 'english') {
        setLocalizedBookName(null);
        return;
      }
      try {
        const { data: books } = await bibleService?.getBibleBooks();
        const bookObj = books?.find(b => b?.name?.toLowerCase() === selectedBook?.toLowerCase());
        if (!bookObj) { setLocalizedBookName(null); return; }
        const localized = await bibleService?.getLocalizedBookName(bookObj?.book_number, versionLanguage);
        setLocalizedBookName(localized || null);
      } catch {
        setLocalizedBookName(null);
      }
    };
    fetchLocalizedName();
  }, [selectedBook, versionLanguage]);

  // Auto-scroll to active verse when it changes during playback
  useEffect(() => {
    if (activeVerse === null || activeVerse === undefined) return;
    if (activeBook !== selectedBook || activeChapter !== selectedChapter) return;
    const el = document.getElementById(`verse-${activeVerse}`);
    if (el) {
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeVerse, activeBook, activeChapter, selectedBook, selectedChapter]);

  const parseVerseReference = (reference) => {
    if (!reference) return null;
    const cleanedReference = reference?.replace(/\([^)]*\)/g, '')?.trim();
    const singleChapterBooks = [
      'Obadiah', 'Philemon', 'Jude', '2 John', '3 John',
      'Obad', 'Phlm', 'Phm', 'Jud', '2Jn', '3Jn', '2John', '3John'
    ];
    const bookAbbreviations = {
      'Gen': 'Genesis', 'Exod': 'Exodus', 'Lev': 'Leviticus', 'Num': 'Numbers', 'Deut': 'Deuteronomy',
      'Josh': 'Joshua', 'Judg': 'Judges', 'Ruth': 'Ruth', '1Sam': '1 Samuel', '2Sam': '2 Samuel',
      '1Kgs': '1 Kings', '2Kgs': '2 Kings', '1Chr': '1 Chronicles', '2Chr': '2 Chronicles',
      'Ezra': 'Ezra', 'Neh': 'Nehemiah', 'Esth': 'Esther', 'Job': 'Job', 'Ps': 'Psalms',
      'Prov': 'Proverbs', 'Eccl': 'Ecclesiastes', 'Song': 'Song of Solomon', 'Isa': 'Isaiah',
      'Jer': 'Jeremiah', 'Lam': 'Lamentations', 'Ezek': 'Ezekiel', 'Dan': 'Daniel',
      'Hos': 'Hosea', 'Joel': 'Joel', 'Amos': 'Amos', 'Obad': 'Obadiah', 'Jonah': 'Jonah',
      'Mic': 'Micah', 'Nah': 'Nahum', 'Hab': 'Habakkuk', 'Zeph': 'Zephaniah', 'Hag': 'Haggai',
      'Zech': 'Zechariah', 'Mal': 'Malachi',
      'Matt': 'Matthew', 'Mark': 'Mark', 'Luke': 'Luke', 'John': 'John', 'Acts': 'Acts',
      'Rom': 'Romans', '1Cor': '1 Corinthians', '2Cor': '2 Corinthians', 'Gal': 'Galatians',
      'Eph': 'Ephesians', 'Phil': 'Philippians', 'Col': 'Colossians', '1Thess': '1 Thessalonians',
      '2Thess': '2 Thessalonians', '1Tim': '1 Timothy', '2Tim': '2 Timothy', 'Titus': 'Titus',
      'Phlm': 'Philemon', 'Heb': 'Hebrews', 'Jas': 'James', '1Pet': '1 Peter', '2Pet': '2 Peter',
      '1John': '1 John', '2John': '2 John', '3John': '3 John', 'Jude': 'Jude', 'Rev': 'Revelation',
      'Ge': 'Genesis', 'Ex': 'Exodus', 'Le': 'Leviticus', 'Nu': 'Numbers', 'De': 'Deuteronomy',
      'Jos': 'Joshua', 'Jdg': 'Judges', 'Ru': 'Ruth', '1Sa': '1 Samuel', '2Sa': '2 Samuel',
      '1Ki': '1 Kings', '2Ki': '2 Kings', '1Ch': '1 Chronicles', '2Ch': '2 Chronicles',
      'Ezr': 'Ezra', 'Ne': 'Nehemiah', 'Es': 'Esther', 'Jb': 'Job', 'Psa': 'Psalms',
      'Pr': 'Proverbs', 'Ec': 'Ecclesiastes', 'So': 'Song of Solomon', 'Is': 'Isaiah',
      'Je': 'Jeremiah', 'La': 'Lamentations', 'Eze': 'Ezekiel', 'Da': 'Daniel',
      'Ho': 'Hosea', 'Joe': 'Joel', 'Am': 'Amos', 'Ob': 'Obadiah', 'Jon': 'Jonah',
      'Mi': 'Micah', 'Na': 'Nahum', 'Hb': 'Habakkuk', 'Zep': 'Zephaniah', 'Hg': 'Haggai',
      'Zec': 'Zechariah', 'Ml': 'Malachi',
      'Mt': 'Matthew', 'Mr': 'Mark', 'Lk': 'Luke', 'Jn': 'John', 'Ac': 'Acts',
      'Ro': 'Romans', '1Co': '1 Corinthians', '2Co': '2 Corinthians', 'Ga': 'Galatians',
      'Ep': 'Ephesians', 'Php': 'Philippians', 'Co': 'Colossians', '1Th': '1 Thessalonians',
      '2Th': '2 Thessalonians', '1Ti': '1 Timothy', '2Ti': '2 Timothy', 'Tit': 'Titus',
      'Phm': 'Philemon', 'He': 'Hebrews', 'Ja': 'James', '1Pe': '1 Peter', '2Pe': '2 Peter',
      '1Jn': '1 John', '2Jn': '2 John', '3Jn': '3 John', 'Jud': 'Jude', 'Re': 'Revelation'
    };

    const matchWithColon = cleanedReference?.trim()?.match(/^([1-3]?\s*[A-Za-z]+)\s+(\d+):(\d+)$/);
    const matchWithoutColon = cleanedReference?.trim()?.match(/^([1-3]?\s*[A-Za-z]+)\s+(\d+)$/);

    let bookName, chapter, verse;

    if (matchWithColon) {
      bookName = matchWithColon?.[1]?.trim();
      chapter = parseInt(matchWithColon?.[2], 10);
      verse = parseInt(matchWithColon?.[3], 10);
    } else if (matchWithoutColon) {
      bookName = matchWithoutColon?.[1]?.trim();
      verse = parseInt(matchWithoutColon?.[2], 10);
      chapter = 1;
    } else {
      return null;
    }

    if (bookAbbreviations?.[bookName]) {
      bookName = bookAbbreviations?.[bookName];
    } else {
      const matchingKey = Object.keys(bookAbbreviations)?.find(
        key => key?.toLowerCase() === bookName?.toLowerCase()
      );
      if (matchingKey) bookName = bookAbbreviations?.[matchingKey];
    }

    const isSingleChapterBook = singleChapterBooks?.some(
      scb => scb?.toLowerCase() === bookName?.toLowerCase()
    );
    if (isSingleChapterBook && matchWithColon) {
      verse = chapter;
      chapter = 1;
    }

    return { book: bookName, chapter, verse };
  };

  const handleVerseReferenceClick = (reference) => {
    const parsed = parseVerseReference(reference);
    if (parsed) {
      const { book, chapter, verse } = parsed;
      setSelectedBook(book);
      setSelectedChapter(chapter);
      setHighlightVerseNumber(verse);
      addHistoryEntry(book, chapter, verse || null);
      setTimeout(() => {
        const verseElement = document?.getElementById(`verse-${verse}`);
        if (verseElement) verseElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) setShowSidebar(false);
      else setShowSidebar(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBookChange = (book) => {
    setSelectedBook(book);
    setSelectedChapter(1);
    setHighlightVerseNumber(null);
    addHistoryEntry(book, 1);
    trackBibleBookChange(book, selectedVersion);
  };

  const handleChapterChange = (chapter) => {
    setSelectedChapter(chapter);
    setHighlightVerseNumber(null);
    addHistoryEntry(selectedBook, chapter);
    trackBibleChapterNavigation(selectedBook, chapter, selectedVersion);
  };

  const handlePreviousChapter = () => {
    if (selectedChapter > 1) {
      const newChapter = selectedChapter - 1;
      setSelectedChapter(newChapter);
      addHistoryEntry(selectedBook, newChapter);
      trackBibleChapterNavigation(selectedBook, newChapter, selectedVersion);
    }
  };

  const handleNextChapter = () => {
    const newChapter = selectedChapter + 1;
    setSelectedChapter(newChapter);
    addHistoryEntry(selectedBook, newChapter);
    trackBibleChapterNavigation(selectedBook, newChapter, selectedVersion);
  };

  const handleVerseSelect = (reference, verseNumber) => {
    setSelectedVerseForStudy(verseNumber);
  };

  const handleAddToLibrary = (verses, reference) => {
    navigate('/verse-library');
  };

  const handleVerseSearchSelect = (verse) => {
    if (verse?.book_name && verse?.chapter_number) {
      setSelectedBook(verse?.book_name);
      setSelectedChapter(verse?.chapter_number);
      if (verse?.verse_number) {
        setHighlightVerseNumber(verse?.verse_number);
        addHistoryEntry(verse?.book_name, verse?.chapter_number, verse?.verse_number);
        setTimeout(() => {
          const verseElement = document?.getElementById(`verse-${verse?.verse_number}`);
          if (verseElement) verseElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      } else {
        addHistoryEntry(verse?.book_name, verse?.chapter_number);
      }
    }
  };

  const handleOpenAudioPlayer = () => {
    openPlayer(selectedBook, selectedChapter);
  };

  const handleEnterFullScreen = () => {
    setIsFullScreen(true);
    if (window.innerWidth < 1024) setShowSidebar(false);
  };

  const handleExitFullScreen = () => {
    setIsFullScreen(false);
  };

  // Navigate to a history entry
  const handleHistoryNavigate = (entry) => {
    setSelectedBook(entry?.book);
    setSelectedChapter(entry?.chapter);
    if (entry?.verse) {
      setHighlightVerseNumber(entry?.verse);
      setTimeout(() => {
        const el = document?.getElementById(`verse-${entry?.verse}`);
        if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } else {
      setHighlightVerseNumber(null);
    }
  };

  const handleClearHistory = () => {
    setNavHistory([]);
  };

  // Determine if audio is playing for this chapter (for pulse dot)
  const isAudioActiveHere = isLoaded && isPlaying && activeBook === selectedBook && activeChapter === selectedChapter;

  // Active verse for highlighting
  const audioActiveVerse = (isLoaded && activeBook === selectedBook && activeChapter === selectedChapter) ? activeVerse : null;

  const sidebarPanels = [
    { id: 'egw-insights', label: 'EGW Insights', icon: 'BookOpen' },
    { id: 'commentaries', label: 'Commentaries', icon: 'BookText' },
    { id: 'tsk', label: 'TSK', icon: 'GitBranch' },
    { id: 'history', label: 'History', icon: 'History' },
    { id: 'progress', label: 'Progress', icon: 'Target' }
  ];

  const handleVersionDataChange = ({ language, hasStrongs }) => {
    setVersionLanguage(language || 'English');
    setVersionHasStrongs(hasStrongs === true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Dynamic SEO: Open Graph, JSON-LD, Meta Description ── */}
      {(() => {
        const bookDisplay = selectedBook
          ? selectedBook?.charAt(0)?.toUpperCase() + selectedBook?.slice(1)
          : 'Bible';
        const verseRef = selectedVerseForStudy
          ? `${bookDisplay} ${selectedChapter}:${selectedVerseForStudy}`
          : `${bookDisplay} ${selectedChapter}`;
        const pageTitle = selectedVerseForStudy
          ? `${verseRef} — Bible Study | Fortified Mind`
          : `${bookDisplay} Chapter ${selectedChapter} — Bible Reader | Fortified Mind`;
        const metaDesc = selectedVerseForStudy
          ? `Read and study ${verseRef} (${selectedVersion?.toUpperCase()}) with EGW insights, commentaries, and cross-references on Fortified Mind.`
          : `Read ${bookDisplay} Chapter ${selectedChapter} in the ${selectedVersion?.toUpperCase()} Bible with study tools including EGW insights, commentaries, and cross-references.`;
        const canonicalUrl = selectedVerseForStudy
          ? `https://fortifiedmind.org/bible-reader?book=${encodeURIComponent(selectedBook)}&chapter=${selectedChapter}&verse=${selectedVerseForStudy}`
          : `https://fortifiedmind.org/bible-reader?book=${encodeURIComponent(selectedBook)}&chapter=${selectedChapter}`;
        const ogImage = 'https://fortifiedmind.org/assets/images/FortifiedMindSplash1-1769379659227.jpeg';

        const jsonLd = {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: pageTitle,
          description: metaDesc,
          url: canonicalUrl,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Fortified Mind',
            url: 'https://fortifiedmind.org',
          },
          about: {
            '@type': 'Book',
            name: 'The Bible',
            bookEdition: selectedVersion?.toUpperCase(),
          },
          ...(selectedVerseForStudy && {
            mainEntity: {
              '@type': 'Quotation',
              name: verseRef,
              isPartOf: {
                '@type': 'Book',
                name: bookDisplay,
                bookEdition: selectedVersion?.toUpperCase(),
              },
            },
          }),
        };

        return (
          <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={metaDesc} />
            <link rel="canonical" href={canonicalUrl} />
            {/* Open Graph */}
            <meta property="og:type" content="article" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={metaDesc} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:site_name" content="Fortified Mind" />
            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={metaDesc} />
            <meta name="twitter:image" content={ogImage} />
            {/* JSON-LD Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify(jsonLd)}
            </script>
          </Helmet>
        );
      })()}
      {!isFullScreen && <Header />}
      <GuestBanner />
      <div className="flex">
        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${showSidebar && !isFullScreen ? 'lg:mr-80' : ''}`}>
          <div className={`${isFullScreen ? 'p-2' : 'p-2 sm:p-3 lg:p-4'} space-y-2 sm:space-y-3`}>

            {/* ── Compact Single-Row Toolbar ── */}
            <div className="bg-card rounded-lg border border-border px-2 sm:px-3 py-2">
              <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap sm:flex-nowrap">

                {/* Book + Chapter Selector */}
                <BookChapterSelector
                  selectedBook={selectedBook}
                  selectedChapter={selectedChapter}
                  onBookChange={handleBookChange}
                  onChapterChange={handleChapterChange}
                  onPreviousChapter={handlePreviousChapter}
                  onNextChapter={handleNextChapter}
                  className="flex-shrink-0"
                  localizedBookName={localizedBookName}
                  versionSelector={
                    <BibleVersionSelector
                      selectedVersion={selectedVersion}
                      onVersionChange={setSelectedVersion}
                      className="flex-shrink-0"
                      onVersionDataChange={handleVersionDataChange}
                    />
                  }
                  searchBar={
                    <button
                      onClick={() => setShowSearchDrawer(true)}
                      title="Search Bible"
                      className="w-7 h-7 flex items-center justify-center rounded border border-border bg-background hover:bg-muted transition-colors"
                      aria-label="Search Bible"
                    >
                      <Icon name="Search" size={14} className="text-foreground" />
                    </button>
                  }
                  toolbarButtons={
                    <>
                      <button
                        onClick={handleOpenAudioPlayer}
                        title="Open Audio Player"
                        className="relative w-7 h-7 flex items-center justify-center rounded border border-border bg-background hover:bg-muted transition-colors"
                        aria-label="Open audio player"
                      >
                        <Icon name="Volume2" size={14} className="text-foreground" />
                        {isAudioActiveHere && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                        )}
                      </button>
                      <button
                        onClick={isFullScreen ? handleExitFullScreen : handleEnterFullScreen}
                        title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
                        className={`w-7 h-7 flex items-center justify-center rounded border transition-colors ${
                          isFullScreen
                            ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted text-foreground'
                        }`}
                        aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
                      >
                        <Icon name={isFullScreen ? 'Minimize2' : 'Maximize2'} size={14} />
                      </button>
                      <button
                        onClick={() => setShowSidebar(!showSidebar)}
                        title={showSidebar ? 'Hide Study Tools' : 'Show Study Tools'}
                        className={`w-7 h-7 flex items-center justify-center rounded border transition-colors ${
                          showSidebar
                            ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted text-foreground'
                        }`}
                        aria-label="Toggle study tools"
                      >
                        <Icon name={showSidebar ? 'PanelRightClose' : 'BookOpenCheck'} size={14} />
                      </button>
                    </>
                  }
                />

                {/* Search Button - Remove this block */}
                {/* Study Tools & Full Screen Buttons - Remove this block */}
              </div>
            </div>

            {/* Bible Text */}
            <BibleTextDisplay
              book={selectedBook}
              chapter={selectedChapter}
              version={selectedVersion}
              onVerseSelect={handleVerseSelect}
              onAddToLibrary={handleAddToLibrary}
              highlightVerseNumber={highlightVerseNumber}
              onVerseReferenceClick={handleVerseReferenceClick}
              audioActiveVerse={audioActiveVerse}
              onVerseNumberAudioClick={seekToVerse}
              hasStrongs={versionHasStrongs}
              localizedBookName={localizedBookName}
            />
          </div>
        </div>

        {/* Sidebar */}
        {showSidebar && !isFullScreen && (
          <div className="fixed right-0 top-16 bottom-0 w-full sm:w-80 bg-background border-l border-border z-30 lg:static lg:top-0">
            <div className="h-full flex flex-col">
              <div className="border-b border-border p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading font-semibold text-foreground">Study Tools</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSidebar(false)}
                    className="lg:hidden min-w-[44px] min-h-[44px]"
                  >
                    <Icon name="X" size={18} />
                  </Button>
                </div>
                <div className="flex items-center space-x-1 mt-2 sm:mt-3 flex-wrap gap-y-1">
                  {sidebarPanels?.map((panel) => (
                    <Button
                      key={panel?.id}
                      variant={activePanel === panel?.id ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setActivePanel(panel?.id)}
                      iconName={panel?.icon}
                      iconPosition="left"
                      className="text-xs min-h-[36px]"
                    >
                      {panel?.label}
                      {panel?.id === 'history' && navHistory?.length > 0 && (
                        <span className="ml-1 bg-primary/20 text-primary text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          {navHistory?.length}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {activePanel === 'progress' && (
                  <ReadingProgressTracker
                    onViewPlans={() => setActivePanel('progress')}
                    className="h-full border-0 shadow-none rounded-none"
                  />
                )}
                {activePanel === 'egw-insights' && (
                  <EGWInsightsDisplay
                    currentBook={selectedBook}
                    currentChapter={selectedChapter}
                    selectedVerse={selectedVerseForStudy}
                  />
                )}
                {activePanel === 'commentaries' && (
                  <div className="h-full overflow-y-auto p-3">
                    {selectedVerseForStudy ? (
                      <CommentariesVerseInsights
                        book={selectedBook}
                        chapter={selectedChapter}
                        verse={selectedVerseForStudy}
                        onClose={() => {}}
                        onVerseReference={handleVerseReferenceClick}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-40 text-center px-4">
                        <Icon name="BookText" size={28} className="text-muted-foreground/40 mb-2" />
                        <p className="text-xs text-muted-foreground">Select a verse to view commentaries.</p>
                      </div>
                    )}
                  </div>
                )}
                {activePanel === 'tsk' && (
                  <div className="h-full overflow-y-auto p-3">
                    {selectedVerseForStudy ? (
                      <TSKDisplay
                        book={selectedBook}
                        chapter={selectedChapter}
                        verse={selectedVerseForStudy}
                        verseId={`${selectedBook}-${selectedChapter}-${selectedVerseForStudy}`}
                        textSizePx={16}
                        isVisible={true}
                        onToggleVisibility={() => {}}
                        onVerseReference={handleVerseReferenceClick}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-40 text-center px-4">
                        <Icon name="GitBranch" size={28} className="text-muted-foreground/40 mb-2" />
                        <p className="text-xs text-muted-foreground">Select a verse to view cross-references.</p>
                      </div>
                    )}
                  </div>
                )}
                {activePanel === 'history' && (
                  <NavigationHistoryPanel
                    history={navHistory}
                    onNavigate={handleHistoryNavigate}
                    onClear={handleClearHistory}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Mobile Overlay */}
      {showSidebar && !isFullScreen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}
      {/* Full Screen Exit Button (mobile) */}
      {isFullScreen && (
        <button
          onClick={handleExitFullScreen}
          className="fixed top-3 right-3 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors shadow-lg"
          aria-label="Exit full screen"
        >
          <Icon name="X" size={20} />
        </button>
      )}
      {/* Audio Player Drawer */}
      <AudioPlayerDrawer />
      {/* Bible Search Drawer */}
      <BibleSearchDrawer
        isOpen={showSearchDrawer}
        onClose={() => setShowSearchDrawer(false)}
        onVerseSelect={handleVerseSearchSelect}
        currentVersion={selectedVersion}
      />
      {/* Floating Play/Pause Button */}
      <FloatingPlayButton />
      {/* Bottom Padding for Mobile Navigation */}
      {!isFullScreen && <div className="h-20 md:hidden" />}
    </div>
  );
};

// Outer wrapper provides the AudioPlayerProvider
const BibleReader = () => {
  return (
    <AudioPlayerProvider>
      <BibleReaderInner />
    </AudioPlayerProvider>
  );
};

export default BibleReader;